import {
    ComponentProps,
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
    AnalyticsFiltersContainer,
    DrawerWrapper,
    Drawer,
    UsageAndCostDataSets,
} from '@/components'
import { UsageAndCostReport } from '@/components/data/UsageAndCost/UsageAndCostReport/UsageAndCostReport'
import { GetReportResponse, useElementWidth, useFiltersStore } from '@/hooks'
import { ReportEditHeader } from './ReportEditHeader'
import {
    DEFAULT_ANALYTICS_FILTERING,
    DEFAULT_USAGE_AND_COST_DATA_SETS,
} from '@/utils/constants'
import { mapFiltersToParams } from './mapFiltersToParams'
import { mapParamsToFilters } from './mapParamsToFilters'
import useNavigationGuardStore from '@/stores/useNavigationGuardStore/useNavigationGuardStore'
import {
    hasNameChanged,
    haveDatasetsChanged,
    haveFiltersChanged,
} from '../../utils/detectReportChange'
import { ResetReportSettingsButton } from '@/components/molecules/ResetReportSettingsButton/ResetReportSettingsButton'
import { processUsageCostFilters } from '../../utils/processUsageCostFilters'
import { TableLoadingOverlay } from '@/components'

type ReportEditUsageAndCostProps = {
    data: GetReportResponse
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
}

export const ReportEditUsageAndCost: React.FC<ReportEditUsageAndCostProps> = (
    props
) => {
    const { t } = useTranslation('translations')
    const [data, setData] = useState(props.data)
    const [fields, setFields] = useState<string[]>(data.fields)
    const [filters, setFilters] = useState(data.filters)
    const [name, setName] = useState(data.name)
    const toggleButtonRef = useRef<HTMLButtonElement>(null)
    const toggleWidth = useElementWidth(toggleButtonRef)

    const parsedFilters = mapParamsToFilters(filters)
    const usageCostRequestParams = processUsageCostFilters(filters)
    const rootNode = useFiltersStore((state) => state.rootNode)

    const baselineFilters = useMemo(() => {
        const params = mapFiltersToParams({
            ...DEFAULT_ANALYTICS_FILTERING,
            node: rootNode?.nodeId !== undefined ? [rootNode.nodeId] : [],
        })

        const { commodityTypeIds: _commodityTypeIds, ...filtered } = params
        return filtered
    }, [rootNode?.nodeId])

    const handleResetFilters = useCallback(() => {
        setFilters(baselineFilters)
    }, [baselineFilters])

    const showResetFiltersButton = useMemo(() => {
        return haveFiltersChanged(filters, baselineFilters)
    }, [filters, baselineFilters])

    const updatedReport = useMemo(
        () => ({
            ...data,
            name,
            filters,
            fields,
        }),
        [data, fields, filters, name]
    )

    const setUnsavedChanges = useNavigationGuardStore(
        (state) => state.setUnsavedChanges
    )
    useEffect(() => {
        const filtersChanged = haveFiltersChanged(filters, data.filters)
        const fieldsChanged = haveDatasetsChanged(fields, data.fields)
        const nameChanged = hasNameChanged(name, data.name)

        setUnsavedChanges(filtersChanged || fieldsChanged || nameChanged)
    }, [
        name,
        fields,
        filters,
        data.name,
        data.fields,
        data.filters,
        setUnsavedChanges,
    ])

    const onFiltersChange = useCallback<OnChangeFn>((filter, value) => {
        setFilters((prevParams) => {
            const prevFilters = mapParamsToFilters(prevParams)

            const newFilters = {
                ...prevFilters,
                [filter]: value,
            }
            const newParams = mapFiltersToParams(newFilters)

            return newParams
        })
    }, [])
    const calculationMethodOffset = useMemo(() => {
        if (!props.isOpen) {
            return toggleWidth
        }

        return 16
    }, [props.isOpen, toggleWidth])

    const handleReportSaved = () => {
        setFields(updatedReport.fields)
        setFilters(updatedReport.filters)
        setName(updatedReport.name)
        setData(updatedReport)
        setUnsavedChanges(false)
    }

    const [isUpdatePending, setIsUpdatePending] = useState(false)

    return (
        <div className="relative grid h-full grid-cols-[100%] grid-rows-[auto_1fr]">
            <div className="relative z-30">
                <ReportEditHeader
                    initialReportName={data.name}
                    report={updatedReport}
                    onChangeName={(reportName: string) => {
                        setName(reportName)
                    }}
                    onSaveAsNewReportSuccess={handleReportSaved}
                    onUpdateReportSuccess={handleReportSaved}
                    onUpdatePendingChange={setIsUpdatePending}
                />
                <div className="border-b border-neutral-200 p-6">
                    <AnalyticsFiltersContainer
                        activeFilters={parsedFilters}
                        onChange={onFiltersChange}
                        handleReset={handleResetFilters}
                        showResetFiltersButton={showResetFiltersButton}
                    />
                </div>
            </div>
            <TableLoadingOverlay
                show={isUpdatePending}
                title={t('reporting.update_report.loading_save')}
                coverContainer
            />
            <DrawerWrapper className="h-full">
                <Drawer
                    headerContent={
                        <ResetReportSettingsButton
                            onClick={() => {
                                setFields(DEFAULT_USAGE_AND_COST_DATA_SETS)
                            }}
                        />
                    }
                    drawerToggleLabel={t(
                        'analytics.common.report_settings_title'
                    )}
                    drawerToggleIconName="database"
                    title={t('analytics.common.report_settings_title')}
                    isOpen={props.isOpen}
                    setIsOpen={props.setIsOpen}
                    toggleButtonRef={toggleButtonRef}
                >
                    <UsageAndCostDataSets
                        activeDataSets={fields}
                        setActiveDataSets={setFields}
                        filters={usageCostRequestParams}
                    />
                </Drawer>
                <UsageAndCostReport
                    filters={usageCostRequestParams}
                    dataSets={fields}
                    isOpen={props.isOpen}
                    calculationMethodOffset={calculationMethodOffset}
                />
            </DrawerWrapper>
        </div>
    )
}

type OnChangeFn = ComponentProps<typeof AnalyticsFiltersContainer>['onChange']
